import { useState, useEffect, useCallback } from 'react'
import { stockApi, produitsApi } from '../api'
import { useApp } from '../AppContext'
import { FormGroup, Empty, Loading } from '../components/ui'

export default function Stock() {
  const { toast, isAdmin }    = useApp()
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [alerteOnly, setAlerteOnly] = useState(false)
  const [detail, setDetail]   = useState(null)  // produit sélectionné
  const [ajustModal, setAjustModal] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    const p = {}
    if (alerteOnly) p.alerte = 1
    stockApi.list(p)
      .then(r => setItems(r.data || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [alerteOnly])

  useEffect(() => { load() }, [load])

  const displayed = items.filter(p =>
    !search || p.designation.toLowerCase().includes(search.toLowerCase()) || (p.reference || '').toLowerCase().includes(search.toLowerCase())
  )

  const enAlerte = items.filter(p => p.en_alerte == 1).length

  return (
    <div className="content">

      {/* Bandeau alerte stock */}
      {enAlerte > 0 && (
        <div style={{ background: '#fce4ec', border: '1px solid #f48fb1', borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="ti ti-alert-triangle" style={{ color: 'var(--danger)', fontSize: 18 }} />
          <span style={{ fontSize: 13, color: '#c62828', fontWeight: 500 }}>
            {enAlerte} produit(s) en dessous du stock minimum
          </span>
          <button className="btn sm" style={{ marginLeft: 'auto', borderColor: '#f48fb1', color: '#c62828' }} onClick={() => setAlerteOnly(a => !a)}>
            {alerteOnly ? 'Voir tout' : 'Voir uniquement les alertes'}
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid3" style={{ marginBottom: 18 }}>
        <div style={kpiStyle('var(--primary-light)', 'var(--primary)')}>
          <i className="ti ti-package" style={{ fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{items.length}</div>
            <div style={{ fontSize: 12 }}>Produits suivis</div>
          </div>
        </div>
        <div style={kpiStyle('#fce4ec', 'var(--danger)')}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{enAlerte}</div>
            <div style={{ fontSize: 12 }}>En alerte stock bas</div>
          </div>
        </div>
        <div style={kpiStyle('#e8f5e9', '#2e7d32')}>
          <i className="ti ti-check" style={{ fontSize: 22 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{items.length - enAlerte}</div>
            <div style={{ fontSize: 12 }}>Stock suffisant</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div className="search-box">
          <i className="ti ti-search" />
          <input
            placeholder="Rechercher un produit…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>{displayed.length} produit(s)</span>
      </div>

      {/* Table stock */}
      {loading ? <Loading /> : displayed.length === 0 ? (
        <Empty icon="package" message="Aucun produit avec suivi de stock — activez le suivi dans le catalogue" />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Catégorie</th>
                  <th>Stock actuel</th>
                  <th>Stock min.</th>
                  <th>État</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {displayed.map(p => {
                  const alerte  = p.en_alerte == 1
                  const pct     = p.stock_min > 0 ? Math.min(100, (p.stock_actuel / p.stock_min) * 100) : 100
                  const barColor = alerte ? 'var(--danger)' : pct < 150 ? 'var(--warning)' : 'var(--success)'

                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(p)}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{p.designation}</div>
                        {p.reference && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{p.reference}</div>}
                      </td>
                      <td>{p.categorie_nom || '—'}</td>
                      <td>
                        <strong style={{ fontSize: 15, color: alerte ? 'var(--danger)' : 'var(--text)' }}>
                          {parseFloat(p.stock_actuel).toFixed(2)}
                        </strong>
                        <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 4 }}>
                          {p.stock_unite || ''}
                        </span>
                      </td>
                      <td>
                        {p.stock_min != null
                          ? <span style={{ fontSize: 13 }}>{parseFloat(p.stock_min).toFixed(2)} {p.stock_unite || ''}</span>
                          : <span style={{ color: 'var(--text3)' }}>—</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 80, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width .3s' }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: barColor }}>
                            {alerte ? '⚠ Bas' : '✓ OK'}
                          </span>
                        </div>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <button
                          className="btn sm"
                          onClick={() => setAjustModal(p)}
                          title="Ajuster le stock"
                        >
                          <i className="ti ti-adjustments" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal détail + mouvements */}
      {detail && (
        <StockDetail
          produit={detail}
          onClose={() => setDetail(null)}
          onAjust={() => { setAjustModal(detail); setDetail(null) }}
        />
      )}

      {/* Modal ajustement */}
      {ajustModal && (
        <AjustementModal
          produit={ajustModal}
          onClose={() => setAjustModal(null)}
          onSaved={() => {
            setAjustModal(null)
            load()
            toast('Stock mis à jour', 'success')
          }}
        />
      )}
    </div>
  )
}

// ── Détail stock + historique mouvements ─────────────────────
function StockDetail({ produit, onClose, onAjust }) {
  const { toast }             = useApp()
  const [mvts, setMvts]       = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    stockApi.mouvements(produit.id)
      .then(r => setMvts(r.data || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [produit.id])

  const fmtDate = d => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

  const alerte = produit.en_alerte == 1

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{produit.designation}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              {produit.reference && <span style={{ marginRight: 10 }}>Réf: {produit.reference}</span>}
              {produit.categorie_nom}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm primary" onClick={onAjust}>
              <i className="ti ti-adjustments" /> Ajuster
            </button>
            <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
          </div>
        </div>

        <div className="modal-body">
          {/* Stock actuel */}
          <div className="grid3" style={{ marginBottom: 18 }}>
            <div style={{ ...kpiStyle(alerte ? '#fce4ec' : 'var(--primary-light)', alerte ? 'var(--danger)' : 'var(--primary)'), padding: '14px' }}>
              <i className="ti ti-package" style={{ fontSize: 20 }} />
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  {parseFloat(produit.stock_actuel).toFixed(2)} {produit.stock_unite || ''}
                </div>
                <div style={{ fontSize: 11 }}>Stock actuel</div>
              </div>
            </div>
            <div style={{ ...kpiStyle('#fff8e1', '#e65100'), padding: '14px' }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 20 }} />
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  {produit.stock_min != null ? `${parseFloat(produit.stock_min).toFixed(2)} ${produit.stock_unite || ''}` : '—'}
                </div>
                <div style={{ fontSize: 11 }}>Stock minimum</div>
              </div>
            </div>
            <div style={{ ...kpiStyle('#e8f5e9', '#2e7d32'), padding: '14px' }}>
              <i className="ti ti-history" style={{ fontSize: 20 }} />
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{mvts.length}</div>
                <div style={{ fontSize: 11 }}>Mouvements</div>
              </div>
            </div>
          </div>

          {/* Historique mouvements */}
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 10 }}>
            Historique des mouvements
          </p>

          {loading ? <Loading /> : mvts.length === 0 ? (
            <Empty icon="history" message="Aucun mouvement enregistré" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Quantité</th>
                    <th>Avant</th>
                    <th>Après</th>
                    <th>Document</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {mvts.map(m => {
                    const isEntree = m.type === 'entree'
                    const isSortie = m.type === 'sortie'
                    return (
                      <tr key={m.id}>
                        <td style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{fmtDate(m.created_at)}</td>
                        <td>
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                            background: isEntree ? '#e8f5e9' : isSortie ? '#fce4ec' : '#e3f2fd',
                            color:      isEntree ? '#2e7d32' : isSortie ? '#c62828' : '#1565c0',
                          }}>
                            {isEntree ? '↑ Entrée' : isSortie ? '↓ Sortie' : '⟳ Ajustement'}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: isEntree ? 'var(--success)' : isSortie ? 'var(--danger)' : 'var(--info)' }}>
                            {isEntree ? '+' : isSortie ? '-' : ''}{parseFloat(m.quantite).toFixed(2)}
                          </strong>
                        </td>
                        <td style={{ color: 'var(--text3)', fontSize: 13 }}>{parseFloat(m.stock_avant).toFixed(2)}</td>
                        <td style={{ fontWeight: 600 }}>{parseFloat(m.stock_apres).toFixed(2)}</td>
                        <td style={{ fontSize: 12 }}>{m.document_numero || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text3)' }}>{m.note || '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Modal ajustement stock ────────────────────────────────────
function AjustementModal({ produit, onClose, onSaved }) {
  const { toast } = useApp()
  const [form, setForm] = useState({ type: 'entree', quantite: '', note: '' })
  const [saving, setSaving] = useState(false)
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }))

  const stockApres = () => {
    const q = parseFloat(form.quantite) || 0
    const actuel = parseFloat(produit.stock_actuel) || 0
    if (form.type === 'entree')     return actuel + q
    if (form.type === 'sortie')     return actuel - q
    if (form.type === 'ajustement') return q
    return actuel
  }

  async function save(e) {
    e.preventDefault(); setSaving(true)
    try {
      await stockApi.ajustement(produit.id, form)
      onSaved()
    } catch (err) { toast(err.message, 'error') }
    finally { setSaving(false) }
  }

  const apercu = stockApres()

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <span className="modal-title">Ajuster le stock — {produit.designation}</span>
          <button className="btn sm icon" onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            {/* Stock actuel */}
            <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--text3)' }}>Stock actuel</span>
              <strong style={{ fontSize: 16 }}>{parseFloat(produit.stock_actuel).toFixed(2)} {produit.stock_unite || ''}</strong>
            </div>

            <FormGroup label="Type de mouvement">
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'entree',     label: '↑ Entrée',     color: '#2e7d32', bg: '#e8f5e9' },
                  { v: 'sortie',     label: '↓ Sortie',     color: '#c62828', bg: '#fce4ec' },
                  { v: 'ajustement', label: '⟳ Ajustement', color: '#1565c0', bg: '#e3f2fd' },
                ].map(t => (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, type: t.v }))}
                    style={{
                      flex: 1, padding: '8px 4px', border: `2px solid ${form.type === t.v ? t.color : 'var(--border)'}`,
                      borderRadius: 'var(--radius)', background: form.type === t.v ? t.bg : 'var(--bg)',
                      color: form.type === t.v ? t.color : 'var(--text3)', fontWeight: form.type === t.v ? 600 : 400,
                      fontSize: 12, cursor: 'pointer', transition: 'all .15s'
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </FormGroup>

            <FormGroup label={form.type === 'ajustement' ? 'Nouveau stock (valeur absolue)' : 'Quantité'} required>
              <input
                type="number"
                className="form-control"
                value={form.quantite}
                onChange={f('quantite')}
                min={0}
                step="any"
                placeholder="0"
                required
                autoFocus
              />
            </FormGroup>

            <FormGroup label="Note (optionnelle)">
              <input className="form-control" value={form.note} onChange={f('note')} placeholder="Ex: Livraison fournisseur, Inventaire…" />
            </FormGroup>

            {/* Aperçu */}
            {form.quantite && (
              <div style={{ background: apercu < 0 ? '#fce4ec' : 'var(--primary-light)', border: `1px solid ${apercu < 0 ? '#f48fb1' : 'var(--g100)'}`, borderRadius: 'var(--radius)', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--text3)' }}>Stock après mouvement</span>
                <strong style={{ fontSize: 16, color: apercu < 0 ? 'var(--danger)' : 'var(--primary)' }}>
                  {apercu.toFixed(2)} {produit.stock_unite || ''}
                  {apercu < 0 && <span style={{ fontSize: 11, marginLeft: 6 }}>⚠ Stock négatif</span>}
                </strong>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving || !form.quantite}>
              {saving ? '…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Style helper ──────────────────────────────────────────────
const kpiStyle = (bg, color) => ({
  background: bg, color,
  border: `1px solid ${color}33`,
  borderRadius: 'var(--radius-lg)',
  padding: '16px 18px',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
})
